import type React from 'react';

/** 同步透明 textarea 与高亮层的滚动位置，避免偶发 caret / 文字错位。 */
export function syncPromptHighlightScroll(
  textarea: HTMLTextAreaElement | null | undefined,
  highlight: HTMLElement | null | undefined,
): void {
  if (!textarea || !highlight) return;
  highlight.scrollTop = textarea.scrollTop;
  highlight.scrollLeft = textarea.scrollLeft;
}

export function bindPromptHighlightOverlaySync(
  textarea: HTMLTextAreaElement,
  highlight: HTMLElement,
): () => void {
  const sync = () => syncPromptHighlightScroll(textarea, highlight);
  sync();
  textarea.addEventListener('scroll', sync, { passive: true });
  window.addEventListener('resize', sync);
  const ro = new ResizeObserver(() => {
    requestAnimationFrame(sync);
  });
  ro.observe(textarea);
  return () => {
    textarea.removeEventListener('scroll', sync);
    window.removeEventListener('resize', sync);
    ro.disconnect();
  };
}

type PromptHighlightOverlayHandlers = Pick<
  React.TextareaHTMLAttributes<HTMLTextAreaElement>,
  'onScroll' | 'onInput' | 'onCompositionStart' | 'onCompositionEnd'
>;

const PROMPT_OVERLAY_WRAP_SELECTOR = '.prompt-ta-wrap, .ig-prompt-wrap';
const PROMPT_OVERLAY_COMPOSING_CLASS = 'prompt-overlay--composing';

function setPromptOverlayComposing(textarea: HTMLTextAreaElement, composing: boolean): void {
  const wrap = textarea.closest(PROMPT_OVERLAY_WRAP_SELECTOR);
  if (!wrap) return;
  wrap.classList.toggle(PROMPT_OVERLAY_COMPOSING_CLASS, composing);
}

/** 绑定 scroll / input / IME 事件，与高亮层保持对齐。 */
export function promptHighlightOverlayHandlers(
  getHighlight: () => HTMLElement | null | undefined,
): PromptHighlightOverlayHandlers {
  const sync = (textarea: HTMLTextAreaElement) => {
    syncPromptHighlightScroll(textarea, getHighlight());
    requestAnimationFrame(() => syncPromptHighlightScroll(textarea, getHighlight()));
  };

  return {
    onScroll: (e) => sync(e.currentTarget),
    onInput: (e) => sync(e.currentTarget),
    onCompositionStart: (e) => {
      setPromptOverlayComposing(e.currentTarget, true);
    },
    onCompositionEnd: (e) => {
      setPromptOverlayComposing(e.currentTarget, false);
      sync(e.currentTarget);
    },
  };
}
